import { useState, useEffect } from "react";
import ChangePassword from "../../../cors/ChangePassword";
import Permissions from "../../../cors/Permissions";
import { ShieldCheck, UserRoundCog } from 'lucide-react';
import { BASE_URL } from '../../../apiConfig';
import Cookies from 'js-cookie';
import { useAuth } from "../../../context/AuthProvider";
import { useLanguage } from "../../../context/LanguageProvider";
import { toast } from "sonner"

export default function ProfileAdmin({ user }) {
  const { token, role } = useAuth();
  const { t } = useLanguage();
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "Male"
  })
  const [message, setMessage] = useState({ type: '', text: '' })

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.admin_info?.name || "",
        email: user.email || "",
        phone: user.admin_info?.phone || "",
        gender: user.admin_info?.gender || "Male"
      })
    }
  }, [user])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSubmit = async () => {
    setIsLoading(true)
    setMessage({ type: '', text: '' })
    
    try {
      const response = await fetch(`${BASE_URL}/api/update-admin-profile/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData)
      })
      
      const result = await response.json()
      
      if (response.ok) {
        setMessage({ type: 'success', text: result.message })
        setIsEditing(false)
        toast.success(t("profile.admin.update_success"))
        console.log(result.user);
        Cookies.set("user", JSON.stringify(result.user), { expires: 7 });

      } else {
        toast.error(t("profile.admin.update_error"))
        setMessage({ type: 'error', text: result.message || t('profile.admin.update_error') })
      }
    } catch (error) {
      setMessage({ type: 'error', text: t('profile.admin.connection_error') })
      toast.error(t("profile.admin.connection_error"))
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    setFormData({
      name: user.admin_info?.name || "",
      email: user.email || "",
      phone: user.admin_info?.phone || "",
      gender: user.admin_info?.gender || "Male"
    })
    setIsEditing(false)
    setMessage({ type: '', text: '' })
  }

  const roleDisplay = role === 1 ? t("profile.admin.title_admin") : t("profile.admin.title_assistant")
  const roleDescription = role === 1 ? t("profile.admin.role_admin_desc") : t("profile.admin.role_assistant_desc")

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-[var(--color-primary-600)] via-[var(--color-primary-500)] to-[var(--color-primary-400)] px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 md:w-32 md:h-32 bg-white/20 rounded-full border-4 border-white shadow-lg flex items-center justify-center">
                {role === 1 ?
                  <ShieldCheck className="h-10 w-10 md:w-16 md:h-16 text-white" />
                  :
                  <UserRoundCog className="h-10 w-10 md:w-16 md:h-16 text-white" />
                }
              </div>
              <div className="text-white">
                <h1 className="text-xl md:text-2xl font-bold">{formData.name}</h1>
                <p className="text-xs text-emerald-100 mb-1">{formData.email}</p>
                <p className="text-emerald-100 text-xs">{roleDescription}</p>
              </div>
            </div>
            <div className="mt-4 md:mt-0">
              <div className="bg-white bg-opacity-2 backdrop-blur-sm rounded-lg px-4 py-2 text-center">
                <div className="text-xl font-bold ">{roleDisplay}</div>
                <div className="text-xs text-emerald-500">{t('profile.admin.trust_score')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* {user.roles && <Permissions permissions={user.roles} />} */}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
        <div className="p-8 max-xl:px-5">
          <div className="space-y-6"> 
            <div className="">
              <div className="mb-4">
                {!isEditing ? (
                  <div className="flex justify-end">
                    <button
                      onClick={() => setIsEditing(true)}
                      className="bg-[var(--color-primary-50)] text-[var(--color-primary-500)]  cursor-pointer px-4 py-2 rounded-lg font-semibold transition-colors duration-200 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <span className="text-sm max-xl:text-xs">{t('profile.admin.edit')}</span>
                    </button>
                  </div>
                ) : (
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={handleSubmit}
                      disabled={isLoading}
                      className={`bg-[var(--color-primary-500)] text-white cursor-pointer px-6 py-2 rounded-lg font-semibold hover:bg-[var(--color-primary-400)] transition-colors duration-200 flex items-center gap-2 ${
                        isLoading ? 'opacity-70' : ''
                      }`}
                    >
                      {isLoading ? (
                        <>
                          <svg className="animate-spin w-3 h-3" fill="none" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          <span className="max-xl:text-xs">{t('profile.admin.saving')}</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-sm max-xl:text-xs">{t('profile.admin.save')}</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleCancel}
                      className="bg-red-100 text-red-600 cursor-pointer px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition-colors duration-200 flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span className="text-sm max-xl:text-xs">{t('profile.admin.cancel')}</span>
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-[var(--color-primary-50)] p-3 rounded-lg">
                  <svg className="w-6 h-6 text-[var(--color-primary-500)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-800">{t('profile.admin.personal_info')}</h2>
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">{t('profile.admin.account_settings')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.admin.full_name')}</label>
                  {isEditing ? 
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    />
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.name || t('profile.admin.not_specified')}
                    </p>
                  }
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.admin.gender')}</label>
                  {isEditing ? 
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    >
                      <option value="Male">{t('admins.male')}</option>
                      <option value="Female">{t('admins.female')}</option>
                    </select>
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.gender || t('profile.admin.not_specified')}
                    </p>
                  }
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.admin.email')}</label>
                  {isEditing ? 
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    />
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.email || t('profile.admin.not_specified')}
                    </p>
                  }
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('profile.admin.phone')}</label>
                  {isEditing ? 
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full py-3 px-4 text-xs border-2 outline-none rounded-2xl transition-all duration-200 bg-gray-50 focus:bg-white border-gray-200 focus:border-[var(--color-primary-500)] focus:ring-4 focus:ring-[var(--color-primary-100)]"
                    />
                    :
                    <p className="text-gray-800 text-xs bg-gray-50 px-4 py-3 rounded-2xl">
                      {formData.phone || t('profile.admin.not_specified')}
                    </p>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
  
      {user && <ChangePassword userId={user.id} />}

    </div>
  )
}